import { useMutation } from '@tanstack/react-query'

import { useSharedDates } from '@contexts/sharedDates'
import { useDate } from '@hooks/dates'
import { IGenericTask } from '@interfaces/task/generic/IGenericTask'
import { queryClient } from '@services/queryClient'

import { usePatchTask } from './patch'

interface IPatchTaskParams {
  id: number
  changes: Partial<IGenericTask>
}

export function useEditTaskFieldAndTaskList() {
  const { patchTask } = usePatchTask()
  const { selectedDate } = useSharedDates()
  const { getWeekRange } = useDate()

  const { weekStart } = getWeekRange(selectedDate)

  return useMutation({
    mutationFn: patchTask,
    onMutate: handleOptimisticTaskListEdit,
    onError: (_err, _newTask, context) => {
      if (!context) return

      const { previousTasks, tasksKey } = context
      queryClient.setQueryData(tasksKey, previousTasks)
    },
    onSettled: (_tasks, _err, _variables, context) => {
      queryClient.invalidateQueries({ queryKey: context?.tasksKey })
    },
  })

  async function handleOptimisticTaskListEdit({
    id,
    changes,
  }: IPatchTaskParams) {
    await queryClient.cancelQueries({
      queryKey: ['TASKS', weekStart.getTime()],
    })
    const [tasksQuery] = queryClient.getQueriesData<IGenericTask[]>({
      queryKey: ['TASKS', weekStart.getTime()],
      type: 'active',
    })

    const [tasksKey, tasksResult] = tasksQuery
    const previousTasks = tasksResult

    const updatedTasks = tasksResult?.map((task) => {
      if (task.id === id) {
        return {
          ...task,
          ...changes,
        }
      }

      return task
    })

    queryClient.setQueryData(tasksKey, () => updatedTasks)

    return {
      tasksKey,
      previousTasks,
      updatedTasks,
    }
  }
}
